import React, { FC, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from 'src/app/modules/auth';
import { getAllMenu } from 'src/app/modules/applications/management-menu/menu-settings/core/request';
import { checkGuardProtection, DataMenuAuthGuard } from 'src/app/modules/auth/core/AuthGuard';
import TopBarProgress from 'react-topbar-progress-indicator';
import { getCSSVariableValue } from 'src/_metronic/assets/ts/_utils';

const whitelistedPrefixes = [
  '/dashboard',
  '/sign-auth',
  '/error',
  '/crafted',
  '/builder',
  '/account',
  '/apps/chat',
  '/menu-test',
];

const RouteGuard: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { auth } = useAuth();
  const location = useLocation();

  // Memanfaatkan cache dari React Query (sama persis dengan SidebarMenuMain)
  const { data: menuData, isLoading } = useQuery({
    queryKey: ['Get All Menu'],
    queryFn: async () => {
      const res = await getAllMenu();
      return res.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 menit cache
  });

  // Extract daftar semua link yang diizinkan dan ditolak untuk user saat ini
  const { allowedLinks, deniedLinks } = useMemo(() => {
    if (!menuData || !auth) return { allowedLinks: [], deniedLinks: [] };

    const allowed: string[] = [];
    const denied: string[] = [];

    // Fungsi rekursif untuk mengekstrak semua link yang ditolak (termasuk anak-anaknya)
    const extractDenied = (items: DataMenuAuthGuard[]) => {
      items.forEach((item) => {
        if (item.link) {
          denied.push(item.link.startsWith('/') ? item.link.slice(1) : item.link);
        }
        if (item.subItems && item.subItems.length > 0) {
          extractDenied(item.subItems);
        }
      });
    };

    // Fungsi rekursif untuk mengecek dan mengumpulkan link
    const extractLinks = (items: DataMenuAuthGuard[]) => {
      items.forEach((item) => {
        const isAllowed = checkGuardProtection(
          {
            title: item.title,
            guards: item.roles || [],
            bidang: item.bidang,
            subBidang: item.sub_bidang,
            subItems: item.subItems,
          },
          auth
        );

        const linkPath = item.link ? (item.link.startsWith('/') ? item.link.slice(1) : item.link) : null;

        if (isAllowed) {
          if (linkPath) allowed.push(linkPath);
          // Lanjutkan cek anak-anaknya karena parent diizinkan
          if (item.subItems && item.subItems.length > 0) {
            extractLinks(item.subItems);
          }
        } else {
          // Jika parent ditolak, maka parent dan SEMUA anaknya otomatis ditolak
          if (linkPath) denied.push(linkPath);
          if (item.subItems && item.subItems.length > 0) {
            extractDenied(item.subItems);
          }
        }
      });
    };

    extractLinks(menuData);
    return { allowedLinks: allowed, deniedLinks: denied };
  }, [menuData, auth]);

  if (isLoading) {
    const baseColor = getCSSVariableValue('--bs-primary');
    TopBarProgress.config({
      barColors: { '0': baseColor },
      barThickness: 1,
      shadowBlur: 5,
    });
    return <TopBarProgress />;
  }

  const currentPath = location.pathname;

  // 1. Cek apakah path ada di whitelist
  const isWhitelisted = whitelistedPrefixes.some((prefix) => currentPath.startsWith(prefix));
  if (isWhitelisted || currentPath === '/') {
    return <>{children}</>;
  }

  const normalizedPath = currentPath.startsWith('/') ? currentPath.slice(1) : currentPath;

  console.log('RouteGuard check:', {
    normalizedPath,
    allowedLinks,
    deniedLinks
  });

  // 2. Prioritas Tertinggi: Cek apakah path secara spesifik DITOLAK
  // Ini mencegah bug di mana child menu ditolak, tapi lolos karena parent-nya diizinkan.
  const isExplicitlyDenied = deniedLinks.some((deniedLink) => {
    if (!deniedLink || deniedLink === '#' || deniedLink === '') return false;
    
    // Jika link ini juga ada di allowedLinks, berarti ini menu duplikat yang salah satunya diizinkan.
    // Jangan ditolak!
    const isAlsoAllowed = allowedLinks.some(
      (allowed) => allowed && allowed.toLowerCase() === deniedLink.toLowerCase()
    );
    if (isAlsoAllowed) return false;

    const pathLower = normalizedPath.toLowerCase();
    const linkLower = deniedLink.toLowerCase();
    return pathLower === linkLower || pathLower.startsWith(`${linkLower}/`);
  });

  if (isExplicitlyDenied) {
    console.warn(`Akses ditolak secara spesifik ke: ${currentPath}. Mengarahkan kembali ke Dashboard.`);
    return <Navigate to='/dashboard' replace />;
  }

  // 3. Cek apakah path cocok dengan salah satu link menu dinamis yang diizinkan
  // - normalizedPath === allowedLink: Cocok persis
  // - normalizedPath.startsWith(`${allowedLink}/`): User mengakses sub-halaman dari menu yang diizinkan
  // - allowedLink.startsWith(`${normalizedPath}/`): User mengakses base path (parent) yang akan me-redirect ke child yang diizinkan
  const isAllowedDynamicRoute = allowedLinks.some((allowedLink) => {
    if (!allowedLink || allowedLink === '#' || allowedLink === '') return false;
    const pathLower = normalizedPath.toLowerCase();
    const linkLower = allowedLink.toLowerCase();
    return pathLower === linkLower || 
    pathLower.startsWith(`${linkLower}/`) ||
    linkLower.startsWith(`${pathLower}/`);
  });

  if (isAllowedDynamicRoute) {
    return <>{children}</>;
  }

  // 3. Jika tidak diizinkan, kembalikan ke dashboard
  console.warn(`Akses ditolak ke: ${currentPath}. Mengarahkan kembali ke Dashboard.`);
  return <Navigate to='/dashboard' replace />;
};

export { RouteGuard };
